import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import LogoMark from '../components/LogoMark';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { setError('Please fill in all fields.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/api/auth/signup`, form);
      localStorage.setItem('knowfi_user', JSON.stringify({
        name: res.data.name,
        email: res.data.email,
        plan: res.data.plan,
        token: res.data.token,
        createdAt: res.data.createdAt,
      }));
      navigate('/account');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <SEO
        title="Create Your Free Account"
        description="Sign up for KnowFi free — get your Money Game Plan, Financial Health Score, AI finance coach, and daily market brief. No credit card required."
        path="/signup"
      />
      <div style={styles.glow} />
      <div style={styles.card}>
        <LogoMark style={{ marginBottom: '24px' }} />
        <h1 style={styles.title}>Create your free account</h1>
        <p style={styles.subtitle}>Start understanding your money today. No credit card required.</p>

        <div style={styles.freeNote}>✅ 5 AI questions/day · Game Plan · Health Score · Daily Brief</div>

        <form onSubmit={submit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input style={styles.input} type="text" placeholder="Jane Smith" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" placeholder="jane@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" placeholder="At least 6 characters" value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }} disabled={loading}>
            {loading ? 'Creating account...' : 'Create Free Account'}
          </button>
        </form>

        <p style={styles.upgradeNote}>Want unlimited AI + Pro features? <Link to="/pricing" style={styles.linkBlue}>See Pro →</Link></p>
        <p style={styles.loginLink}>Already have an account? <Link to="/login" style={styles.linkBlue}>Log in</Link></p>
        <p style={styles.legal}>By creating an account, you agree to our Terms. KnowFi provides educational content only — not investment advice.</p>
      </div>
    </div>
  );
}

const styles = {
  page: { background: '#0f172a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative' },
  glow: { position: 'fixed', top: '0', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '600px', background: 'radial-gradient(ellipse at center top, rgba(56,189,248,0.1) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 },
  card: { background: '#1e293b', borderRadius: '20px', padding: '40px', width: '100%', maxWidth: '440px', border: '1px solid #334155', position: 'relative', zIndex: 1 },
  title: { fontSize: '26px', fontWeight: '800', marginBottom: '8px', letterSpacing: '-0.3px' },
  subtitle: { color: '#64748b', fontSize: '15px', marginBottom: '20px' },
  freeNote: { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#38bdf8', marginBottom: '24px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '12px 14px', fontSize: '15px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', width: '100%' },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '14px' },
  btn: { background: '#38bdf8', color: '#0f172a', border: 'none', padding: '14px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', cursor: 'pointer', fontFamily: 'inherit', marginTop: '4px' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  upgradeNote: { textAlign: 'center', color: '#64748b', fontSize: '14px', marginTop: '16px' },
  loginLink: { textAlign: 'center', color: '#64748b', fontSize: '14px', marginTop: '8px' },
  linkBlue: { color: '#38bdf8', textDecoration: 'none', fontWeight: '600' },
  legal: { color: '#334155', fontSize: '11px', textAlign: 'center', marginTop: '16px', lineHeight: 1.5 },
};

export default Signup;
